import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import {
  GET_MY_PROFILE,
  UPDATE_NOTIFICATION_SOUND_PREFERENCE,
  UPDATE_PRESENCE
} from '../../graphql/operations';
import {
  USER_SELECTABLE_NOTIFICATION_SOUND_PRESETS,
  resolveNotificationSoundType
} from '../../contexts/notificationSoundCatalog';
import authService from '../../auth/authService';
import config from '../../config';
import SidebarHeader from './SidebarHeader';
import FavoritesSection from './FavoritesSection';
import ChannelSection from './ChannelSection';
import DMSection from './DMSection';
import AISection from './AISection';
import QuickReactChipsSettings from '../chat/QuickReactChipsSettings';
import './Sidebar.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

function Sidebar({ onToggleSidebar, isSidebarCollapsed = false }) {
  const {
    loading,
    selectedConversation,
    selectConversation,
    notificationSoundEnabled,
    notificationSoundType,
    prepareNotificationSound,
    testNotificationSound
  } = useContext(WorkspaceContext);

  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const { data: profileData, refetch: refetchProfile } = useQuery(GET_MY_PROFILE);
  const [updatePresenceMutation] = useMutation(UPDATE_PRESENCE);
  const [updateNotificationSoundPreferenceMutation] = useMutation(
    UPDATE_NOTIFICATION_SOUND_PREFERENCE,
    {
      update: (cache, { data }) => {
        const updatedPreference = data?.updateNotificationSoundPreference;
        if (!updatedPreference) return;

        const existing = cache.readQuery({ query: GET_MY_PROFILE });
        const currentProfile = existing?.getMyProfile || {};
        cache.writeQuery({
          query: GET_MY_PROFILE,
          data: {
            getMyProfile: {
              ...currentProfile,
              ...updatedPreference
            }
          }
        });
      }
    }
  );

  const profile = profileData?.getMyProfile;
  const displayName = profile?.displayName || authService.getEmail()?.split('@')[0] || 'User';
  const profileEmail = profile?.email || authService.getEmail() || profile?.username || '';

  const canAccessUserManagement =
    authService.hasRole('system_admin') || authService.hasRole('admin');
  const canAccessCostDashboard =
    authService.hasRole('system_admin') || authService.hasRole('admin');
  const voiceOpsEnabled = Boolean(config.features?.voiceOpsEnabled);
  const canAccessVoiceLive =
    voiceOpsEnabled && (authService.hasRole('system_admin') || authService.hasRole('admin'));
  const canAccessVoiceAdmin =
    voiceOpsEnabled && (authService.hasRole('system_admin') || authService.hasRole('admin'));

  // Profile popover state
  const [showPopover, setShowPopover] = useState(false);
  const [appearOffline, setAppearOffline] = useState(
    profile?.presenceOverride === 'offline'
  );
  const [statusText, setStatusText] = useState(profile?.statusMessage || '');
  const [soundEnabled, setSoundEnabled] = useState(notificationSoundEnabled !== false);
  const [soundType, setSoundType] = useState(resolveNotificationSoundType(notificationSoundType));
  const [soundStatus, setSoundStatus] = useState('');
  const [presenceStatus, setPresenceStatus] = useState('');
  const [isTestingSound, setIsTestingSound] = useState(false);
  const soundStatusTimeoutRef = useRef(null);
  const presenceStatusTimeoutRef = useRef(null);
  const popoverRef = useRef(null);

  // Sync from profile when it loads
  useEffect(() => {
    if (profile) {
      setAppearOffline(profile.presenceOverride === 'offline');
      setStatusText(profile.statusMessage || '');
    }
  }, [profile]);

  useEffect(() => {
    setSoundEnabled(notificationSoundEnabled !== false);
  }, [notificationSoundEnabled]);

  useEffect(() => {
    setSoundType(resolveNotificationSoundType(notificationSoundType));
  }, [notificationSoundType]);

  useEffect(() => {
    return () => {
      if (soundStatusTimeoutRef.current) {
        clearTimeout(soundStatusTimeoutRef.current);
      }
      if (presenceStatusTimeoutRef.current) {
        clearTimeout(presenceStatusTimeoutRef.current);
      }
    };
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPopover]);

  const handleSelectConversation = useCallback(
    (conversation) => {
      selectConversation(conversation);
      if (!location.pathname.startsWith('/conversations')) {
        navigate('/conversations');
      }
    },
    [selectConversation, navigate, location.pathname]
  );

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  const setTransientPresenceStatus = useCallback((nextStatus) => {
    setPresenceStatus(nextStatus);
    if (presenceStatusTimeoutRef.current) {
      clearTimeout(presenceStatusTimeoutRef.current);
    }
    presenceStatusTimeoutRef.current = setTimeout(() => {
      setPresenceStatus('');
    }, 2200);
  }, []);

  const setTransientSoundStatus = useCallback((nextStatus) => {
    setSoundStatus(nextStatus);
    if (soundStatusTimeoutRef.current) {
      clearTimeout(soundStatusTimeoutRef.current);
    }
    soundStatusTimeoutRef.current = setTimeout(() => {
      setSoundStatus('');
    }, 2400);
  }, []);

  const persistPresence = useCallback(async ({ presenceOverride, statusMessage, successMessage }) => {
    try {
      const result = await updatePresenceMutation({
        variables: { presenceOverride, statusMessage }
      });
      const updated = result?.data?.updatePresence;
      if (updated) {
        setAppearOffline(updated.presenceOverride === 'offline');
        setStatusText(updated.statusMessage || '');
      }
      setTransientPresenceStatus(successMessage);
      refetchProfile().catch(() => {});
      return true;
    } catch (_error) {
      setTransientPresenceStatus('Unable to save. Please try again.');
      return false;
    }
  }, [refetchProfile, setTransientPresenceStatus, updatePresenceMutation]);

  const handleToggleOffline = async () => {
    const nextOffline = !appearOffline;
    setAppearOffline(nextOffline);
    const saved = await persistPresence({
      presenceOverride: nextOffline ? 'offline' : 'auto',
      statusMessage: statusText.trim() || null,
      successMessage: nextOffline ? 'Appear offline enabled.' : 'Appear offline disabled.'
    });
    if (!saved) {
      setAppearOffline(!nextOffline);
    }
  };

  const handleToggleSound = async () => {
    const nextValue = !soundEnabled;
    setSoundEnabled(nextValue);
    setSoundStatus('');
    try {
      await updateNotificationSoundPreferenceMutation({
        variables: { enabled: nextValue }
      });
      await apolloClient.refetchQueries({ include: [GET_MY_PROFILE] });
      setTransientSoundStatus('Sound preference saved.');
    } catch (_error) {
      setSoundEnabled(!nextValue);
      setTransientSoundStatus('Failed to save. Please try again.');
    }
  };

  const handleChangeSoundType = async (event) => {
    const nextType = resolveNotificationSoundType(event.target.value);
    const previousType = soundType;
    setSoundType(nextType);
    setSoundStatus('');
    try {
      await updateNotificationSoundPreferenceMutation({
        variables: { soundType: nextType }
      });
      await apolloClient.refetchQueries({ include: [GET_MY_PROFILE] });
      setTransientSoundStatus('Sound selection saved.');
    } catch (_error) {
      setSoundType(previousType);
      setTransientSoundStatus('Failed to save sound choice.');
    }
  };

  const handleTestSound = async () => {
    if (isTestingSound) return;
    setIsTestingSound(true);
    setSoundStatus('');
    try {
      const result = await testNotificationSound({ soundType });
      if (result?.played) {
        setTransientSoundStatus('Ping played.');
      } else if (result?.reason === 'disabled') {
        setTransientSoundStatus('Sound is turned off.');
      } else if (result?.reason === 'unsupported') {
        setTransientSoundStatus('Audio is not supported in this browser.');
      } else if (result?.reason === 'load_error') {
        setTransientSoundStatus('Sound file could not load.');
      } else if (result?.reason === 'timeout') {
        setTransientSoundStatus('Sound timed out. Try another preset.');
      } else {
        setTransientSoundStatus('Sound is blocked. Click again after interacting with the page.');
      }
    } catch (_err) {
      setTransientSoundStatus('Unable to test sound right now.');
    } finally {
      setIsTestingSound(false);
    }
  };

  const handlePrepareSound = () => {
    prepareNotificationSound({ soundType });
  };

  const handleStatusSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = statusText.trim();
      persistPresence({
        presenceOverride: appearOffline ? 'offline' : 'auto',
        statusMessage: trimmed || null,
        successMessage: trimmed ? 'Status saved.' : 'Status cleared.'
      });
    }
  };

  const handleClearStatus = () => {
    setStatusText('');
    persistPresence({
      presenceOverride: appearOffline ? 'offline' : 'auto',
      statusMessage: null,
      successMessage: 'Status cleared.'
    });
  };

  return (
    <div className="app-sidebar">
      <SidebarHeader
        onToggleSidebar={onToggleSidebar}
        isSidebarCollapsed={isSidebarCollapsed}
      />
      <nav className="sidebar-nav" aria-label="Primary">
        <NavLink
          to="/activity"
          className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v1.382a1 1 0 11-2 0V4H6v12h8V9.618a1 1 0 112 0V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm9.293 3.293a1 1 0 011.414 0L18 10.586l-3.293 3.293a1 1 0 01-1.414-1.414L14.586 11H9a1 1 0 110-2h5.586l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Activity Ledger
        </NavLink>
        <NavLink
          to="/bookmarks"
          className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true">
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-3-5 3V4z" />
          </svg>
          Bookmarks
        </NavLink>
        {canAccessUserManagement && (
          <NavLink
            to="/admin/users"
            className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
          >
            <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            User Management
          </NavLink>
        )}
        {canAccessCostDashboard && (
          <NavLink
            to="/admin/costs"
            className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
          >
            <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
              <path d="M3 3.75A1.75 1.75 0 014.75 2h10.5A1.75 1.75 0 0117 3.75v12.5A1.75 1.75 0 0115.25 18H4.75A1.75 1.75 0 013 16.25V3.75zm2 .25v12h10V4H5zm2 2.5a1 1 0 100 2h6a1 1 0 100-2H7zm0 3.5a1 1 0 000 2h4a1 1 0 100-2H7z" />
            </svg>
            Cost Dashboard
          </NavLink>
        )}
        {canAccessVoiceLive && (
          <NavLink
            to="/voice/live"
            className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
          >
            <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true">
              <path d="M4 8a6 6 0 1112 0v1a3 3 0 01-3 3h-1.2a2.8 2.8 0 01-2.8 2.5H8a1 1 0 110-2h1a.8.8 0 00.8-.5H9a3 3 0 01-3-3V8zm6-4a4 4 0 00-4 4v1a1 1 0 001 1h6a1 1 0 001-1V8a4 4 0 00-4-4z" />
            </svg>
            Voice — Live
          </NavLink>
        )}
        {canAccessVoiceAdmin && (
          <NavLink
            to="/admin/voice"
            className={({ isActive }) => `sidebar-nav-link ${isActive ? 'active' : ''}`}
          >
            <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 1l8 4v5c0 4.5-3.4 8.4-8 9-4.6-.6-8-4.5-8-9V5l8-4zm0 2.2L4 6v4c0 3.4 2.5 6.5 6 7 3.5-.5 6-3.6 6-7V6l-6-2.8zM10 7a1 1 0 011 1v3a1 1 0 11-2 0V8a1 1 0 011-1zm0 6.5a1 1 0 110 2 1 1 0 010-2z" clipRule="evenodd" />
            </svg>
            Voice — Admin
          </NavLink>
        )}
      </nav>
      <div className="sidebar-sections">
        {loading ? (
          <div className="sidebar-loading">Loading...</div>
        ) : (
          <>
            <FavoritesSection
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
            />
            <div className="sidebar-search sidebar-search-above-channels">
              <input
                className="sidebar-search-input"
                type="text"
                placeholder="Search conversations..."
                readOnly
              />
            </div>
            <ChannelSection
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
            />
            <DMSection
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
            />
            <AISection
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
            />
          </>
        )}
      </div>
      <div className="sidebar-footer" style={{ position: 'relative' }}>
        <button
          type="button"
          className="sidebar-user-identity sidebar-user-btn"
          onPointerDown={handlePrepareSound}
          onFocus={handlePrepareSound}
          onClick={() => setShowPopover((s) => !s)}
          aria-label="User menu"
        >
          <span className="sidebar-user-avatar">{getInitials(displayName)}</span>
          <span className="sidebar-user-name">{displayName}</span>
          {appearOffline && <span className="sidebar-offline-badge">Offline</span>}
        </button>
        {showPopover && (
          <div className="sidebar-user-popover" ref={popoverRef}>
            <div className="popover-profile-header">
              <span className="popover-profile-avatar" aria-hidden="true">
                {getInitials(displayName)}
              </span>
              <div className="popover-profile-copy">
                <span className="popover-profile-name">{displayName}</span>
                {profileEmail && <span className="popover-profile-email">{profileEmail}</span>}
              </div>
            </div>
            <div className="popover-section popover-setting-card">
              <div className="popover-section-title">Presence</div>
              <label className="popover-toggle-row">
                <span>
                  <span className="popover-toggle-label">Appear offline</span>
                  <span className="popover-toggle-description">Hide active status from teammates.</span>
                </span>
                <button
                  type="button"
                  className={`popover-toggle-switch ${appearOffline ? 'active' : ''}`}
                  onClick={handleToggleOffline}
                  role="switch"
                  aria-checked={appearOffline}
                >
                  <span className="popover-toggle-knob" />
                </button>
              </label>
              <label className="popover-field-label" htmlFor="profile-status-message">
                Set a status
              </label>
              <div className="popover-status-input-row">
                <input
                  id="profile-status-message"
                  type="text"
                  className="popover-status-input"
                  value={statusText}
                  onChange={(e) => setStatusText(e.target.value.slice(0, 100))}
                  onKeyDown={handleStatusSubmit}
                  placeholder="What's your status?"
                  maxLength={100}
                />
                {statusText && (
                  <button
                    type="button"
                    className="popover-status-clear"
                    onClick={handleClearStatus}
                    aria-label="Clear status"
                  >
                    &times;
                  </button>
                )}
              </div>
              <span className="popover-hint">Press Enter to save</span>
              {presenceStatus && (
                <span className="popover-presence-status" role="status" aria-live="polite">
                  {presenceStatus}
                </span>
              )}
            </div>
            <div className="popover-section popover-setting-card">
              <div className="popover-section-title">Notifications</div>
              <label className="popover-toggle-row">
                <span>
                  <span className="popover-toggle-label">Message ping sound</span>
                  <span className="popover-toggle-description">Play a ping for unread DMs and channels.</span>
                </span>
                <button
                  type="button"
                  className={`popover-toggle-switch ${soundEnabled ? 'active' : ''}`}
                  onClick={handleToggleSound}
                  role="switch"
                  aria-checked={soundEnabled}
                  aria-label="Toggle message ping sound"
                >
                  <span className="popover-toggle-knob" />
                </button>
              </label>
              <div className="popover-sound-grid">
                <label className="popover-field-label sound-select-label" htmlFor="notification-sound-type">
                  Sound
                </label>
                <select
                  id="notification-sound-type"
                  className="popover-sound-select"
                  value={soundType}
                  onChange={handleChangeSoundType}
                >
                  {USER_SELECTABLE_NOTIFICATION_SOUND_PRESETS.map((preset) => (
                    <option key={preset.type} value={preset.type}>
                      {preset.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="popover-inline-btn"
                  onPointerDown={handlePrepareSound}
                  onFocus={handlePrepareSound}
                  onClick={handleTestSound}
                  disabled={isTestingSound}
                >
                  {isTestingSound ? 'Testing...' : 'Test sound'}
                </button>
              </div>
              {soundStatus && (
                <span className="popover-inline-status" role="status" aria-live="polite">
                  {soundStatus}
                </span>
              )}
            </div>
            <QuickReactChipsSettings profile={profile} />
            <div className="popover-divider" />
            <button
              type="button"
              className="popover-signout-btn"
              onClick={handleLogout}
            >
              <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
              </svg>
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
